import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostComposer } from "@/components/profile/PostComposer";
import { ProfilePostCard } from "@/components/profile/ProfilePostCard";

export function ProfilePosts({ userId, isOwner, highlightId }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState({});

  const loadMine = useCallback((list) => {
    const ids = list.map((p) => p.id);
    if (!ids.length || !user) return;
    api.get("/profile-reactions/mine", { params: { post_ids: ids.join(",") } }).then((r) => setMine((m) => ({ ...m, ...r.data }))).catch(() => {});
  }, [user]);

  const load = useCallback(async (after) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/members/${userId}/posts`, { params: after ? { cursor: after } : {} });
      let list = data.items;
      if (!after && highlightId && !list.some((p) => p.id === highlightId)) {
        const hp = await api.get(`/profile/posts/${highlightId}`).then((r) => r.data).catch(() => null);
        if (hp && hp.user_id === userId) list = [hp, ...list];
      }
      setItems((prev) => (after && prev ? [...prev, ...list] : list));
      setCursor(data.next_cursor);
      loadMine(list);
    } catch { setItems((prev) => prev || []); }
    finally { setBusy(false); }
  }, [userId, highlightId, loadMine]);

  useEffect(() => { load(null); }, [load]);

  const canInteract = !!user && user.email_verified !== false;
  return (
    <section data-testid="profile-posts" className="mb-6">
      <h2 className="font-display font-bold text-lg mb-3">Publicações</h2>
      {isOwner && <div className="mb-3"><PostComposer onPosted={() => load(null)} /></div>}
      {items === null && <div className="text-sm text-zinc-500 py-4 text-center">A carregar…</div>}
      {items && items.length === 0 && (
        <p data-testid="profile-posts-empty" className="text-sm text-zinc-500 italic text-center py-6 bg-white rounded-2xl border border-dashed border-zinc-300">
          {isOwner ? "Partilhe a sua primeira atualização." : "Este membro ainda não publicou nada."}
        </p>
      )}
      {items && items.length > 0 && (
        <div className="space-y-3">
          {items.map((p) => <ProfilePostCard key={p.id} post={p} mine={mine[p.id] ?? null} canInteract={canInteract} highlighted={p.id === highlightId} isAdmin={user?.role === "admin"} onChange={() => load(null)} />)}
          {cursor && <button type="button" onClick={() => load(cursor)} disabled={busy} data-testid="profile-posts-more" className="mx-auto block text-xs font-semibold px-4 py-2 rounded-full bg-white border border-zinc-200">Ver mais</button>}
        </div>
      )}
    </section>
  );
}
